import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Wallet, TrendingUp, ArrowUpRight, ArrowDownRight, Send,
  User, Phone, Mail, FileText, ChevronDown, Bitcoin, DollarSign,
  Shield, Clock, MessageSquare, HelpCircle, CheckCircle2, XCircle,
  LogOut, CreditCard, Banknote, ArrowRightLeft, Check
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from 'recharts';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Info } from 'lucide-react';
import PositionsModal from '../../components/PositionsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Info tooltip component - appears on hover
const InfoTooltip = ({ text }) => (
  <div className="relative inline-flex group" data-testid="info-tooltip">
    <Info className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help hover:text-muted-foreground transition-colors" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-popover border border-border shadow-lg text-xs text-popover-foreground w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-border" />
      {text}
    </div>
  </div>
);

// Mapping des symboles vers leurs logos
const ASSET_LOGOS = {
  // Crypto - jsdelivr CDN (reliable, no CORS)
  'BTC': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/btc.png',
  'ETH': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/eth.png',
  'BNB': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/bnb.png',
  'XRP': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xrp.png',
  'ADA': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ada.png',
  'SOL': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/sol.png',
  'DOGE': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/doge.png',
  'DOT': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/dot.png',
  'MATIC': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/matic.png',
  'AVAX': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/avax.png',
  'LINK': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/link.png',
  'ATOM': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/atom.png',
  'LTC': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/ltc.png',
  'UNI': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/uni.png',
  'SHIB': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/shib.png',
  'XVG': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/xvg.png',
  'AAVE': 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@1a63530be6e374711a8554f31b17e4cb92c25fa5/128/color/aave.png',
};

// Brand colors for stocks/commodities/forex fallback (no external image needed)
const ASSET_BRAND_COLORS = {
  'GOOGL': '#4285F4', 'AAPL': '#A2AAAD', 'MSFT': '#00A4EF', 'AMZN': '#FF9900',
  'TSLA': '#CC0000', 'META': '#0668E1', 'NVDA': '#76B900', 'NFLX': '#E50914',
  'JPM': '#003A70', 'DIS': '#0057A8', 'AMD': '#ED1C24', 'INTC': '#0071C5',
  'V': '#1A1F71', 'WMT': '#0071DC', 'PFE': '#0093D0', 'MRNA': '#00A1E0',
  'BIIB': '#00857C', 'JNJ': '#D51F37', 'BAC': '#012169',
  'GOLD': '#FFD700', 'SILVER': '#C0C0C0', 'OIL': '#1a1a1a', 'NATGAS': '#FF6B35', 'WHEAT': '#DEB887',
  'EURUSD': '#003399', 'GBPUSD': '#012169', 'USDJPY': '#BC002D',
};

// Mapping des noms d'assets
const ASSET_NAMES = {
  BTC:'Bitcoin',ETH:'Ethereum',XRP:'Ripple',SOL:'Solana',XVG:'Verge',DOGE:'Dogecoin',ADA:'Cardano',DOT:'Polkadot',LINK:'Chainlink',AVAX:'Avalanche',BNB:'BNB',MATIC:'Polygon',ATOM:'Cosmos',LTC:'Litecoin',UNI:'Uniswap',SHIB:'Shiba Inu',FIL:'Filecoin',NEAR:'NEAR Protocol',APT:'Aptos',OP:'Optimism',ARB:'Arbitrum',INJ:'Injective',RNDR:'Render',FET:'Fetch.ai',SUI:'Sui',SEI:'Sei',TIA:'Celestia',PEPE:'Pepe',WIF:'dogwifhat',CRO:'Cronos',ALGO:'Algorand',VET:'VeChain',HBAR:'Hedera',ICP:'Internet Computer',GRT:'The Graph',SAND:'The Sandbox',MANA:'Decentraland',AXS:'Axie Infinity',EGLD:'MultiversX',XLM:'Stellar',EOS:'EOS',XTZ:'Tezos',FLOW:'Flow',ENS:'Ethereum Name',CRV:'Curve',MKR:'Maker',COMP:'Compound',THETA:'Theta',FTM:'Fantom',
  GOOGL:'Alphabet',AAPL:'Apple',MSFT:'Microsoft',AMZN:'Amazon',TSLA:'Tesla',META:'Meta',NVDA:'NVIDIA',NFLX:'Netflix',AMD:'AMD',INTC:'Intel',DIS:'Disney',V:'Visa',JPM:'JPMorgan Chase',WMT:'Walmart',BAC:'Bank of America',KO:'Coca-Cola',PEP:'PepsiCo',MA:'Mastercard',COST:'Costco',CRM:'Salesforce',ADBE:'Adobe',PYPL:'PayPal',UBER:'Uber',ABNB:'Airbnb',BA:'Boeing',GS:'Goldman Sachs',MS:'Morgan Stanley',BLK:'BlackRock',T:'AT&T',CSCO:'Cisco',ORCL:'Oracle',IBM:'IBM',QCOM:'Qualcomm',TXN:'Texas Instruments',NOW:'ServiceNow',ISRG:'Intuitive Surgical',PANW:'Palo Alto Networks',SNOW:'Snowflake',SQ:'Block (Square)',SHOP:'Shopify',COIN:'Coinbase',PLTR:'Palantir',RIVN:'Rivian',SPOT:'Spotify',ROKU:'Roku',ZM:'Zoom',DDOG:'Datadog',CRWD:'CrowdStrike',NET:'Cloudflare',MDB:'MongoDB',LULU:'Lululemon',HD:'Home Depot',LOW:"Lowe's",UNH:'UnitedHealth',JNJ:'Johnson & Johnson',PFE:'Pfizer',ABBV:'AbbVie',MRK:'Merck',XOM:'ExxonMobil',CVX:'Chevron',PG:'Procter & Gamble',NKE:'Nike',MCD:"McDonald's",SBUX:'Starbucks',F:'Ford',GM:'General Motors',AAL:'American Airlines',DAL:'Delta Airlines',
  MC:'LVMH',OR:"L'Oréal",RMS:'Hermès',TTE:'TotalEnergies',SAN:'Sanofi',AI:'Air Liquide',SU:'Schneider',BNP:'BNP Paribas',DG:'Vinci',AIR:'Airbus',CS:'AXA',SAF:'Safran',DSY:'Dassault Systèmes',KER:'Kering',RI:'Pernod Ricard',EL:'EssilorLuxottica',CAP:'Capgemini',SGO:'Saint-Gobain',BN:'Danone',ORA:'Orange',EN:'Bouygues',GLE:'Société Générale',ACA:'Crédit Agricole',VIV:'Vivendi',STMPA:'STMicroelectronics',PUB:'Publicis',LR:'Legrand',ML:'Michelin',HO:'Thales',WLN:'Worldline',RNO:'Renault',URW:'Unibail-Rodamco',VIE:'Veolia',ALO:'Alstom',ENGI:'Engie',CA:'Carrefour',CDI:'Christian Dior',
  SPX:'S&P 500',NDX:'Nasdaq 100',DJI:'Dow Jones',DAX:'DAX 40',CAC40:'CAC 40',FTSE:'FTSE 100',NIKKEI:'Nikkei 225',
  GOLD:'Or (XAU)',SILVER:'Argent (XAG)',OIL:'Pétrole WTI',BRENT:'Pétrole Brent',NATGAS:'Gaz Naturel',WHEAT:'Blé',CORN:'Maïs',COPPER:'Cuivre',
  EURUSD:'EUR/USD',GBPUSD:'GBP/USD',USDJPY:'USD/JPY',USDCHF:'USD/CHF',AUDUSD:'AUD/USD',DXY:'Dollar Index',
  MRNA:'Moderna',BIIB:'Biogen',GILD:'Gilead Sciences',REGN:'Regeneron',VRTX:'Vertex Pharma',AMGN:'Amgen',BMY:'Bristol-Myers',LLY:'Eli Lilly',NVO:'Novo Nordisk',AZN:'AstraZeneca',GSK:'GSK',CRSP:'CRISPR Therapeutics',
};

// Couleurs de fond par catégorie
const ASSET_BG_COLORS = {
  'crypto': 'bg-amber-500/20',
  'stocks': 'bg-blue-500/20',
  'forex': 'bg-green-500/20',
  'commodities': 'bg-orange-500/20',
};

// Ticker with live micro-fluctuations
function TickerLive({ data }) {
  const trackRef = useRef(null);
  const prevPricesRef = useRef({});
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (!trackRef.current) return;

    const update = () => {
      if (!trackRef.current) return;
      const now = Date.now() / 1000;
      trackRef.current.querySelectorAll('[data-ticker-item]').forEach((el) => {
        const basePrice = parseFloat(el.dataset.price);
        const baseChange = parseFloat(el.dataset.change);
        const sym = el.dataset.sym;
        if (!basePrice) return;

        // Update base price from latest data
        const fresh = dataRef.current.find(d => d.symbol === sym);
        if (fresh && fresh.price) {
          el.dataset.price = fresh.price;
          el.dataset.change = fresh.change;
        }

        const seed = now * 5.17 + basePrice * 2.3;
        const noise = Math.sin(seed) * 0.0004 + Math.sin(seed * 2.7) * 0.0003;
        const livePrice = (fresh?.price || basePrice) * (1 + noise);
        const liveChange = (fresh?.change || baseChange) + noise * 100;

        const prev = prevPricesRef.current[sym] || livePrice;
        const dir = livePrice > prev + 0.0001 ? 'up' : livePrice < prev - 0.0001 ? 'down' : '';
        prevPricesRef.current[sym] = livePrice;

        const priceEl = el.querySelector('[data-price]');
        const changeEl = el.querySelector('[data-change]');
        if (priceEl) {
          const fmt = livePrice >= 1000 ? livePrice.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : livePrice >= 1 ? livePrice.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : livePrice.toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
          priceEl.textContent = fmt + ' €';
          priceEl.className = `font-bold font-mono text-sm tabular-nums transition-colors duration-300 ${dir === 'up' ? 'text-green-400' : dir === 'down' ? 'text-red-400' : liveChange >= 0 ? 'text-green-400' : 'text-red-400'}`;
        }
        if (changeEl) {
          const arrow = dir === 'up' ? '▲ ' : dir === 'down' ? '▼ ' : liveChange >= 0 ? '▲ ' : '▼ ';
          changeEl.textContent = `${arrow}${liveChange >= 0 ? '+' : ''}${liveChange.toFixed(2)}%`;
          changeEl.className = `text-[10px] font-bold px-2 py-0.5 rounded-md transition-all duration-300 ${dir === 'up' ? 'text-green-400 bg-green-500/20' : dir === 'down' ? 'text-red-400 bg-red-500/20' : liveChange >= 0 ? 'text-green-400 bg-green-500/15' : 'text-red-400 bg-red-500/15'}`;
        }
      });
    };

    const interval = setInterval(update, 1500);
    update();
    return () => clearInterval(interval);
  }, []); // Empty deps — never re-create, use refs for data

  // Render once with initial data, never re-render
  const [initialData] = useState(() => [...data, ...data]);

  return (
    <div className="ticker-track" ref={trackRef} suppressHydrationWarning>
      {initialData.map((item, index) => (
        <div key={`${item.symbol}-${index}`} data-ticker-item="" data-sym={item.symbol} data-price={item.price} data-change={item.change}
          className="flex items-center gap-2.5 px-5 border-r border-border/10 whitespace-nowrap group">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black ${item.change >= 0 ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
            {item.icon || item.symbol.charAt(0)}
          </div>
          <div>
            <span className="text-[11px] font-bold text-foreground tracking-wide">{item.symbol}</span>
            <span className="text-[9px] text-muted-foreground ml-1.5 hidden sm:inline">{item.name}</span>
          </div>
          <span data-price="" className={`font-bold font-mono text-sm tabular-nums ${item.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {item.price >= 1000 ? item.price.toLocaleString('fr-FR', { maximumFractionDigits: 0 }) : item.price >= 1 ? item.price.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : item.price.toLocaleString('fr-FR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
            <span className="text-[9px] text-muted-foreground ml-0.5">€</span>
          </span>
          <span data-change="" className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${item.change >= 0 ? 'text-green-400 bg-green-500/15' : 'text-red-400 bg-red-500/15'}`}>
            {item.change >= 0 ? '▲' : '▼'} {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
          </span>
        </div>
      ))}
    </div>
  );
}

export default function ClientDashboard() {
  const { token, user, mirrorMode, logout, enterMirrorMode } = useAuth();
  const { branding, currentTemplate, loadMedia } = useTheme();

  // Load heavy media (background/video) only on dashboard
  useEffect(() => { loadMedia(); }, [loadMedia]);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [positions, setPositions] = useState([]);
  const [kycStatus, setKycStatus] = useState(null);
  const [kycDocs, setKycDocs] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confidenceIndex, setConfidenceIndex] = useState(100);
  const [tradingPerformance, setTradingPerformance] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("aujourd'hui");
  const [tickerData, setTickerData] = useState([]);
  
  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showPositionsModal, setShowPositionsModal] = useState(false);
  const [transferData, setTransferData] = useState({
    amount: '',
    direction: 'available_to_trading',
    acceptedTerms: false,
    acceptedConditions: false
  });
  const [transferring, setTransferring] = useState(false);
  const [tradingPortfolio, setTradingPortfolio] = useState(null);
  const [tradingPortfolioEnabled, setTradingPortfolioEnabled] = useState(false);
  const [cryptoPortfolio, setCryptoPortfolio] = useState(null);
  const [pendingAmounts, setPendingAmounts] = useState({
    pendingDeposits: 0,      // Dépôts en attente (+)
    pendingWithdrawals: 0,   // Retraits en attente (-)
    pendingToTrading: 0,     // Transferts vers Trading en attente
    pendingFromTrading: 0    // Transferts depuis Trading en attente
  });
  const [featuredNews, setFeaturedNews] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [historyTransactions, setHistoryTransactions] = useState([]);

  // Open transfer modal from URL param
  useEffect(() => {
    if (searchParams.get('action') === 'transfer') {
      setShowTransferModal(true);
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch real-time market prices for ticker
  const fetchTickerData = async () => {
    try {
      // Fetch crypto prices from CoinGecko
      const cryptoIds = 'bitcoin,ethereum,ripple,solana,cardano,dogecoin,polkadot,chainlink,avalanche-2,matic-network';
      const cryptoResponse = await axios.get(
        `https://api.coingecko.com/api/v3/simple/price?ids=${cryptoIds}&vs_currencies=eur&include_24hr_change=true`
      );
      
      const cryptoData = cryptoResponse.data;
      
      // Map API data to ticker format
      const cryptoTicker = [
        { symbol: 'BTC', name: 'BITCOIN', price: cryptoData.bitcoin?.eur || 0, change: cryptoData.bitcoin?.eur_24h_change || 0, icon: '₿' },
        { symbol: 'ETH', name: 'ETHEREUM', price: cryptoData.ethereum?.eur || 0, change: cryptoData.ethereum?.eur_24h_change || 0, icon: 'Ξ' },
        { symbol: 'XRP', name: 'RIPPLE', price: cryptoData.ripple?.eur || 0, change: cryptoData.ripple?.eur_24h_change || 0, icon: '✕' },
        { symbol: 'SOL', name: 'SOLANA', price: cryptoData.solana?.eur || 0, change: cryptoData.solana?.eur_24h_change || 0, icon: '◎' },
        { symbol: 'ADA', name: 'CARDANO', price: cryptoData.cardano?.eur || 0, change: cryptoData.cardano?.eur_24h_change || 0, icon: '₳' },
        { symbol: 'DOGE', name: 'DOGECOIN', price: cryptoData.dogecoin?.eur || 0, change: cryptoData.dogecoin?.eur_24h_change || 0, icon: 'Ð' },
        { symbol: 'DOT', name: 'POLKADOT', price: cryptoData.polkadot?.eur || 0, change: cryptoData.polkadot?.eur_24h_change || 0, icon: '●' },
        { symbol: 'LINK', name: 'CHAINLINK', price: cryptoData.chainlink?.eur || 0, change: cryptoData.chainlink?.eur_24h_change || 0, icon: '⬡' },
        { symbol: 'AVAX', name: 'AVALANCHE', price: cryptoData['avalanche-2']?.eur || 0, change: cryptoData['avalanche-2']?.eur_24h_change || 0, icon: '▲' },
        { symbol: 'MATIC', name: 'POLYGON', price: cryptoData['matic-network']?.eur || 0, change: cryptoData['matic-network']?.eur_24h_change || 0, icon: '⬢' },
      ];

      // Add forex and stocks with simulated live prices (small random variations)
      const forexStocks = [
        { symbol: 'EUR/USD', name: 'EUR to USD', price: 1.0823 + (Math.random() - 0.5) * 0.002, change: (Math.random() - 0.5) * 0.5, icon: '€' },
        { symbol: 'EUR/JPY', name: 'EUR to JPY', price: 162.45 + (Math.random() - 0.5) * 0.5, change: (Math.random() - 0.5) * 0.3, icon: '¥' },
        { symbol: 'AAPL', name: 'APPLE', price: 234.56 + (Math.random() - 0.5) * 2, change: (Math.random() - 0.5) * 3, icon: '' },
        { symbol: 'MSFT', name: 'MICROSOFT', price: 415.30 + (Math.random() - 0.5) * 3, change: (Math.random() - 0.5) * 2, icon: '' },
        { symbol: 'AMZN', name: 'AMAZON', price: 198.34 + (Math.random() - 0.5) * 2, change: (Math.random() - 0.5) * 2.5, icon: '' },
        { symbol: 'GOLD', name: 'GOLD', price: 2678.90 + (Math.random() - 0.5) * 10, change: (Math.random() - 0.5) * 1.5, icon: '🥇' },
        { symbol: 'SILVER', name: 'SILVER', price: 31.23 + (Math.random() - 0.5) * 0.3, change: (Math.random() - 0.5) * 2, icon: '🥈' },
        { symbol: 'TSLA', name: 'TESLA', price: 345.67 + (Math.random() - 0.5) * 5, change: (Math.random() - 0.5) * 4, icon: '' },
        { symbol: 'BRENT', name: 'BRENT', price: 73.45 + (Math.random() - 0.5) * 1, change: (Math.random() - 0.5) * 2, icon: '🛢️' },
        { symbol: 'NVDA', name: 'NVIDIA', price: 142.80 + (Math.random() - 0.5) * 3, change: (Math.random() - 0.5) * 3.5, icon: '' },
      ];

      setTickerData([...cryptoTicker, ...forexStocks]);
    } catch (error) {
      console.error('Failed to fetch ticker data:', error);
      // Use fallback data if API fails
      setTickerData([
        { symbol: 'BTC', name: 'BITCOIN', price: 97234.50, change: -2.34, icon: '₿' },
        { symbol: 'ETH', name: 'ETHEREUM', price: 3456.78, change: 1.23, icon: 'Ξ' },
        { symbol: 'EUR/USD', name: 'EUR to USD', price: 1.0823, change: -0.15, icon: '€' },
        { symbol: 'GOLD', name: 'GOLD', price: 2678.90, change: 0.45, icon: '🥇' },
        { symbol: 'AAPL', name: 'APPLE', price: 234.56, change: 2.60, icon: '' },
        { symbol: 'MSFT', name: 'MICROSOFT', price: 415.30, change: 0.72, icon: '' },
      ]);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTickerData();
    // Refresh ticker every 30 seconds
    const tickerInterval = setInterval(fetchTickerData, 30000);
    return () => clearInterval(tickerInterval);
  }, [token, user?.id]);

  const fetchData = async () => {
    try {
      // Pass client_id as query param - needed for mirror mode where token is admin's
      const statsUrl = mirrorMode && user?.id 
        ? `${API}/stats/dashboard?client_id=${user.id}`
        : `${API}/stats/dashboard`;
      
      const [statsRes, profileRes, positionsRes, kycStatusRes, kycDocsRes, performanceRes, partnersRes, tradingPortfolioRes, tradingEnabledRes, pendingTxRes, pendingTransfersRes, cryptoPortfolioRes] = await Promise.all([
        axios.get(statsUrl, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/clients/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/trade-positions/${user.id}?sort_order=desc&limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/kyc-status/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/kyc/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/trade-positions/${user.id}/performance`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/partners`).catch(() => ({ data: [] })),
        axios.get(`${API}/portfolio/trading/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/portfolio/trading-enabled/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { enabled: false } })),
        axios.get(`${API}/transactions/${user.id}?limit=500`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { transactions: [] } })),
        axios.get(`${API}/portfolio/transfers`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/crypto/portfolio`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setProfile(profileRes.data);
          const positionsData = Array.isArray(positionsRes.data)
            ? positionsRes.data
            : (positionsRes.data?.positions || []);
          setPositions(positionsData);
      setKycStatus(kycStatusRes.data);
      setKycDocs(kycDocsRes.data || []);
      setTradingPerformance(performanceRes.data);
      setPartners(partnersRes.data || []);
      setTradingPortfolio(tradingPortfolioRes.data);
      setTradingPortfolioEnabled(tradingEnabledRes.data?.enabled || false);
      setCryptoPortfolio(cryptoPortfolioRes.data);
      
      // Calculate pending amounts
      const transactions = pendingTxRes.data?.transactions || [];
      setHistoryTransactions(transactions);
      const transfers = pendingTransfersRes.data || [];
      const pendingStatuses = ['en_attente', 'en_verification', 'en_cours', 'pending'];
      
      const pendingDeposits = transactions
        .filter(t => ['depot', 'deposit'].includes(t.transaction_type) && pendingStatuses.includes(t.status))
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const pendingWithdrawals = transactions
        .filter(t => ['retrait', 'withdrawal'].includes(t.transaction_type) && pendingStatuses.includes(t.status))
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const pendingToTrading = transfers
        .filter(t => t.direction === 'available_to_trading' && t.status === 'pending')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      const pendingFromTrading = transfers
        .filter(t => t.direction === 'trading_to_available' && t.status === 'pending')
        .reduce((sum, t) => sum + (t.amount || 0), 0);
      
      setPendingAmounts({
        pendingDeposits,
        pendingWithdrawals,
        pendingToTrading,
        pendingFromTrading
      });

      // Fetch featured news and products
      const [newsRes, featuredProdsRes] = await Promise.all([
        axios.get(`${API}/news?featured=true&limit=6`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { articles: [] } })),
        axios.get(`${API}/placements/products/featured`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      setFeaturedNews(newsRes.data?.articles || []);
      setFeaturedProducts(featuredProdsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setStats({
        total_capital: 0,
        total_available: 0,
        trading_profit: 0,
        active_contracts: 0,
        pending_requests: 0,
        portfolios: []
      });
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = currentTemplate?.colors?.primary || 'hsl(var(--primary))';
  const accentColor = currentTemplate?.colors?.accent || currentTemplate?.colors?.primary || '#10B981';

  const clientCode = profile?.client_code || `${user?.first_name?.[0] || 'C'}${user?.last_name?.[0] || 'L'}${Math.floor(Math.random() * 900000 + 100000)}`;

  // Generate chart data from real transactions timeline
  const generateChartData = () => {
    const validatedStatuses = ['valide', 'validé', 'validated', 'approved', 'completed', 'active'];
    const creditTypes = ['depot', 'deposit', 'bonus', 'interest', 'interet', 'trade_profit', 'credit', 'ajout_livret'];
    const debitTypes = ['retrait', 'withdrawal', 'debit', 'retrait_livret'];
    
    // Filter only validated transactions
    const validTx = historyTransactions.filter(t => validatedStatuses.includes(t.status));
    
    if (validTx.length === 0) {
      const tradBal = tradingPortfolio?.balance || tradingPortfolio?.available || 0;
      const current = (stats?.total_available || 0) + tradBal + (cryptoPortfolio?.total_value || 0);
      return [{ date: 'Actuel', value: current }];
    }
    
    // Parse dates and sort chronologically
    const txWithDates = validTx.map(t => {
      let dateStr = t.validated_at || t.created_at || '';
      let d = dateStr ? new Date(dateStr) : null;
      if (!d || isNaN(d.getTime())) {
        // Try DD/MM/YYYY format
        const parts = (t.date || '').split('/');
        if (parts.length === 3) d = new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return { ...t, _date: d };
    }).filter(t => t._date && !isNaN(t._date.getTime()))
      .sort((a, b) => a._date - b._date);
    
    if (txWithDates.length === 0) {
      const tradBal = tradingPortfolio?.balance || tradingPortfolio?.available || 0;
      const current = (stats?.total_available || 0) + tradBal + (cryptoPortfolio?.total_value || 0);
      return [{ date: 'Actuel', value: current }];
    }
    
    // Group by month (YYYY-MM)
    const monthly = {};
    txWithDates.forEach(t => {
      const key = `${t._date.getFullYear()}-${String(t._date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthly[key]) monthly[key] = { credits: 0, debits: 0 };
      const amt = Math.abs(t.amount || 0);
      if (creditTypes.includes(t.transaction_type)) {
        monthly[key].credits += amt;
      } else if (debitTypes.includes(t.transaction_type)) {
        monthly[key].debits += amt;
      }
    });
    
    // Add closed position profits grouped by month
    const safePositions = Array.isArray(positions) ? positions : [];
    safePositions.filter(p => p.status === 'closed' && p.credited).forEach(pos => {
      let d = pos.close_at ? new Date(pos.close_at) : null;
      if (!d || isNaN(d.getTime())) {
        const parts = (pos.trade_date || '').split('/');
        if (parts.length === 3) d = new Date(parts[2], parts[1] - 1, parts[0]);
      }
      if (d && !isNaN(d.getTime())) {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!monthly[key]) monthly[key] = { credits: 0, debits: 0 };
        const profit = pos.profit || 0;
        if (profit >= 0) monthly[key].credits += profit;
        else monthly[key].debits += Math.abs(profit);
      }
    });
    
    // Build cumulative timeline (last 12 months max)
    const months = Object.keys(monthly).sort();
    const recentMonths = months.slice(-12);
    
    const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    let runningBalance = 0;
    
    // Calculate balance up to the first displayed month
    months.filter(m => !recentMonths.includes(m)).forEach(m => {
      runningBalance += monthly[m].credits - monthly[m].debits;
    });
    
    const chartData = recentMonths.map(m => {
      const data = monthly[m];
      runningBalance += data.credits - data.debits;
      const [, month] = m.split('-');
      return {
        date: monthNames[parseInt(month) - 1],
        value: Math.round(Math.max(0, runningBalance) * 100) / 100
      };
    });
    
    // Add current point - use TOTAL capital (available + trading + placements + crypto)
    const tradingBalance = tradingPortfolio?.balance || tradingPortfolio?.available || 0;
    const placementsTotal = (stats?.portfolios || [])
      .filter(p => p.type === 'savings' || p.type === 'placement')
      .reduce((sum, p) => sum + (p.balance || p.available || 0), 0);
    const currentTotal = (stats?.total_available || 0) + tradingBalance + placementsTotal + (cryptoPortfolio?.total_value || 0);
    chartData.push({ date: 'Actuel', value: Math.round(currentTotal * 100) / 100 });
    
    return chartData;
  };

  const analystAvailability = useMemo(() => {
    const analyst = profile?.analyst;
    const dayLabels = {
      0: 'Dim',
      1: 'Lun',
      2: 'Mar',
      3: 'Mer',
      4: 'Jeu',
      5: 'Ven',
      6: 'Sam'
    };

    if (!analyst) {
      return {
        label: 'Indisponible',
        detail: 'Aucun conseiller',
        isAvailable: false,
        schedule: ''
      };
    }

    const manualAvailable = analyst.availability_status !== false;
    const days = Array.isArray(analyst.availability_days) ? analyst.availability_days : [];
    const start = analyst.availability_start;
    const end = analyst.availability_end;
    const scheduleDays = days.length ? days.map((d) => dayLabels[d]).join(', ') : 'Non définis';
    const scheduleHours = start && end ? `${start} - ${end}` : 'Horaires non définis';

    if (!manualAvailable) {
      return {
        label: 'Indisponible',
        detail: 'Hors service',
        isAvailable: false,
        schedule: `${scheduleDays} · ${scheduleHours}`
      };
    }

    if (!days.length || !start || !end) {
      return {
        label: 'Disponible',
        detail: 'Disponible',
        isAvailable: true,
        schedule: `${scheduleDays} · ${scheduleHours}`
      };
    }

    const now = new Date();
    const currentDay = now.getDay();
    const toMinutes = (time) => {
      const [hours, minutes] = time.split(':').map((val) => parseInt(val, 10));
      return hours * 60 + minutes;
    };
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const isWithin = days.includes(currentDay) && currentMinutes >= toMinutes(start) && currentMinutes <= toMinutes(end);

    return {
      label: isWithin ? 'Disponible' : 'Indisponible',
      detail: isWithin ? 'Disponible' : 'Hors horaires',
      isAvailable: isWithin,
      schedule: `${scheduleDays} · ${scheduleHours}`
    };
  }, [profile]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </DashboardLayout>
    );
  }

  // Helper function to extract YouTube video ID from embed code or URL
  const getYouTubeEmbedUrl = () => {
    const autoplay = branding?.welcomeVideoAutoplay ? '&autoplay=1&mute=1' : '';
    
    if (branding?.welcomeVideoEmbed) {
      // Extract src from iframe
      const srcMatch = branding.welcomeVideoEmbed.match(/src="([^"]+)"/);
      if (srcMatch) {
        let url = srcMatch[1];
        // Extract video ID
        const videoIdMatch = url.match(/embed\/([^?&"]+)/);
        if (videoIdMatch) {
          // Use youtube-nocookie.com for privacy and cleaner embed
          return `https://www.youtube-nocookie.com/embed/${videoIdMatch[1]}?modestbranding=1&rel=0&showinfo=0&controls=1&iv_load_policy=3&fs=1&playsinline=1${autoplay}`;
        }
        return url;
      }
    }
    if (branding?.welcomeVideoUrl?.includes('youtube')) {
      const videoId = branding.welcomeVideoUrl.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube-nocookie.com/embed/${videoId}?modestbranding=1&rel=0&showinfo=0&controls=1&iv_load_policy=3&fs=1&playsinline=1${autoplay}`;
      }
    }
    return null;
  };

  // Video height mapping
  const getVideoHeight = () => {
    const heights = {
      'small': '150px',
      'medium': '250px',
      'large': '350px',
      'auto': 'auto'
    };
    return heights[branding?.welcomeVideoHeight || 'medium'] || '250px';
  };

  // Mobile-friendly video height
  const getVideoResponsiveStyle = () => {
    const h = getVideoHeight();
    if (h === 'auto') {
      return { aspectRatio: '16/9' };
    }
    // On mobile, use aspect-ratio instead of fixed height
    return {
      height: h,
      maxHeight: '60vh',
    };
  };

  // Video crop styles
  const getVideoCropStyles = () => {
    const cropTop = branding?.welcomeVideoCropTop || 0;
    const cropBottom = branding?.welcomeVideoCropBottom || 0;
    
    if (cropTop === 0 && cropBottom === 0) {
      return {};
    }
    
    // Scale up the video and position it to crop
    const scale = 100 / (100 - cropTop - cropBottom);
    const translateY = -cropTop * scale / 100;
    
    return {
      transform: `scale(${scale}) translateY(${translateY}%)`,
      transformOrigin: 'center center'
    };
  };
  
  // Handle portfolio transfer submission
  const handleTransferSubmit = async () => {
    if (!transferData.acceptedTerms || !transferData.acceptedConditions) {
      toast.error('Veuillez accepter les termes et conditions');
      return;
    }
    
    const amount = parseFloat(transferData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    
    // Check available balance based on direction
    const availableBalance = stats?.total_available || 0;
    const tradingBalance = tradingPortfolio?.available || tradingPortfolio?.balance || 0;
    
    if (transferData.direction === 'available_to_trading' && amount > availableBalance) {
      toast.error(`Solde insuffisant. Disponible: ${availableBalance.toFixed(2)}€`);
      return;
    }
    if (transferData.direction === 'trading_to_available' && amount > tradingBalance) {
      toast.error(`Solde trading insuffisant. Disponible: ${tradingBalance.toFixed(2)}€`);
      return;
    }
    
    setTransferring(true);
    try {
      await axios.post(`${API}/portfolio/transfer`, {
        amount,
        direction: transferData.direction,
        accepted_terms: true,
        accepted_conditions: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Demande de transfert envoyée. En attente de validation.');
      setShowTransferModal(false);
      setTransferData({
        amount: '',
        direction: 'available_to_trading',
        acceptedTerms: false,
        acceptedConditions: false
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferring(false);
    }
  };

  return (
    <DashboardLayout>
      {/* Transfer Modal */}
      <PositionsModal open={showPositionsModal} onClose={() => setShowPositionsModal(false)} />
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-purple-500" />
              Transférer des fonds
            </DialogTitle>
            <DialogDescription>
              Transférez des fonds entre vos portefeuilles. Cette demande nécessite une validation.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Balance display */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-secondary/30">
              <div>
                <p className="text-sm text-muted-foreground">Fonds Disponibles</p>
                <p className="text-xl font-bold" style={{ color: accentColor }}>
                  {(stats?.total_available || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Portefeuille Trading</p>
                <p className="text-xl font-bold text-purple-500">
                  {(tradingPortfolio?.available || tradingPortfolio?.balance || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </p>
              </div>
            </div>
            
            {/* Direction */}
            <div>
              <Label>Direction du transfert</Label>
              <Select 
                value={transferData.direction} 
                onValueChange={(v) => setTransferData(prev => ({ ...prev, direction: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available_to_trading">
                    Fonds Disponibles → Trading
                  </SelectItem>
                  <SelectItem value="trading_to_available">
                    Trading → Fonds Disponibles
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Amount */}
            <div>
              <Label>Montant (€)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={transferData.amount}
                onChange={(e) => setTransferData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {transferData.direction === 'available_to_trading' 
                  ? `Maximum disponible: ${(stats?.total_available || 0).toFixed(2)}€`
                  : `Maximum disponible: ${(tradingPortfolio?.available || tradingPortfolio?.balance || 0).toFixed(2)}€`
                }
              </p>
            </div>
            
            {/* Terms and conditions */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  checked={transferData.acceptedTerms}
                  onCheckedChange={(checked) => setTransferData(prev => ({ ...prev, acceptedTerms: checked }))}
                />
                <Label htmlFor="terms" className="text-sm cursor-pointer">
                  Bon pour accord
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="conditions"
                  checked={transferData.acceptedConditions}
                  onCheckedChange={(checked) => setTransferData(prev => ({ ...prev, acceptedConditions: checked }))}
                />
                <Label htmlFor="conditions" className="text-sm cursor-pointer">
                  J'accepte les termes et conditions
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleTransferSubmit}
              disabled={transferring || !transferData.acceptedTerms || !transferData.acceptedConditions || !transferData.amount}
              style={{ backgroundColor: accentColor }}
            >
              {transferring ? 'Envoi...' : 'Envoyer la demande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <div className="space-y-6 relative" data-testid="client-dashboard">
        {/* Header with user info */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg sm:text-2xl font-bold uppercase tracking-wide" style={{ fontFamily: 'var(--font-heading)' }}>
                {user?.first_name} {user?.last_name}
              </h1>
              {profile?.account_type === 'professionnel' && profile?.company_name && (
                <p className="text-sm text-muted-foreground mt-0.5">{profile.company_name}</p>
              )}
            </div>
            {profile?.account_type === 'professionnel' && (
              <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-500 border border-amber-500/30">
                Compte Professionnel
              </span>
            )}
            {(user?.is_test || profile?.is_test) && (
              <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/15 text-red-500 border border-red-500/30 animate-pulse">
                CLIENT TEST
              </span>
            )}
          </div>
          {/* Trading badge */}
          <div className="flex items-center gap-2 flex-wrap">
            {tradingPortfolioEnabled && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-500/30 bg-purple-500/5">
                <span className="text-xs text-purple-400">Trading</span>
                <span className="text-sm font-semibold text-purple-500">
                  {(tradingPortfolio?.available || tradingPortfolio?.balance || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                </span>
                {(pendingAmounts.pendingToTrading > 0 || pendingAmounts.pendingFromTrading > 0) && (
                  <span className="text-[10px] text-yellow-500">
                    {pendingAmounts.pendingToTrading > 0 && `+${pendingAmounts.pendingToTrading.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€`}
                    {pendingAmounts.pendingToTrading > 0 && pendingAmounts.pendingFromTrading > 0 && ' '}
                    {pendingAmounts.pendingFromTrading > 0 && `-${pendingAmounts.pendingFromTrading.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}€`}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Top Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Montant disponible */}
          <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="card-available">
            <CardContent className="p-5">
              <p className="text-xs font-medium text-center uppercase tracking-wider text-muted-foreground mb-2">
                Montant disponible
              </p>
              <p className="text-2xl font-bold text-center" style={{ color: primaryColor }}>
                {(stats?.total_available || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </p>
              {(pendingAmounts.pendingDeposits > 0 || pendingAmounts.pendingWithdrawals > 0 || pendingAmounts.pendingToTrading > 0 || pendingAmounts.pendingFromTrading > 0) && (
                <div className="mt-2 text-xs space-y-0.5 text-center">
                  {pendingAmounts.pendingDeposits > 0 && (
                    <p className="text-yellow-500">À venir: +{pendingAmounts.pendingDeposits.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (dépôt)</p>
                  )}
                  {pendingAmounts.pendingWithdrawals > 0 && (
                    <p className="text-yellow-500">À venir: -{pendingAmounts.pendingWithdrawals.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (retrait)</p>
                  )}
                  {pendingAmounts.pendingToTrading > 0 && (
                    <p className="text-yellow-500">À venir: -{pendingAmounts.pendingToTrading.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (→ trading)</p>
                  )}
                  {pendingAmounts.pendingFromTrading > 0 && (
                    <p className="text-yellow-500">À venir: +{pendingAmounts.pendingFromTrading.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € (← trading)</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dynamique d'évolution */}
          <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="card-progression">
            <CardContent className="p-5">
              <div className="flex items-center justify-center gap-1.5 mb-2">
                <p className="text-xs font-medium text-center uppercase tracking-wider text-muted-foreground">
                  Dynamique d'évolution
                </p>
                <InfoTooltip text="Ce montant représente la somme de tous vos gains et pertes de trading depuis l'ouverture de votre compte. Le pourcentage est calculé par rapport à votre apport financier total." />
              </div>
              <p className="text-xs text-muted-foreground text-center">Gains Trading</p>
              <p className="text-2xl font-bold text-center" style={{ color: (stats?.trading_profit || 0) >= 0 ? accentColor : '#ef5350' }}>
                {(stats?.trading_profit || 0) >= 0 ? '+' : ''}{(stats?.trading_profit || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </p>
              {(() => {
                const capital = (stats?.apport_financier ?? stats?.total_capital) || 0;
                const profit = stats?.trading_profit || 0;
                const pct = capital > 0 ? (profit / capital * 100) : 0;
                return (
                  <p className="text-sm font-semibold text-center mt-1" style={{ color: pct >= 0 ? accentColor : '#ef5350' }}>
                    {pct >= 0 ? '+' : ''}{pct.toFixed(2)}%
                  </p>
                );
              })()}
            </CardContent>
          </Card>

          {/* Apport financier */}
          <Card className="bg-card/80 backdrop-blur border-border/50" data-testid="card-invested">
            <CardContent className="p-5">
              <p className="text-xs font-medium text-center uppercase tracking-wider text-muted-foreground mb-2">
                Apport financier
              </p>
              <p className="text-xs text-muted-foreground text-center">Capital investi</p>
              <p className="text-2xl font-bold text-center" data-testid="client-dashboard-apport-financier">
                {((stats?.apport_financier ?? stats?.total_capital) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
              </p>
            </CardContent>
          </Card>
          
        </div>

        {/* Live Market Ticker */}
        {tickerData.length > 0 && (
          <div className="relative overflow-hidden bg-gradient-to-r from-background via-card to-background border border-border/20 rounded-xl py-1">
            <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10" />
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-live" />
              <span className="text-[9px] font-bold text-red-400 uppercase tracking-widest">Live</span>
            </div>
            <style>
              {`
                @keyframes ticker-scroll {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                .ticker-track {
                  display: flex;
                  animation: ticker-scroll 45s linear infinite;
                  width: max-content;
                  padding-left: 60px;
                }
                .ticker-track:hover {
                  animation-play-state: paused;
                }
              `}
            </style>
            <TickerLive data={tickerData} />
          </div>
        )}

        {/* Welcome Video Section */}
        {branding?.welcomeVideoEnabled && (branding?.welcomeVideoUrl || branding?.welcomeVideoEmbed || branding?.welcomeVideoFile) && branding?.welcomeVideoPosition === 'top' && (
          <Card className="bg-card/80 backdrop-blur border-border/50 overflow-hidden">
            {branding?.welcomeVideoTitle && (
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{branding.welcomeVideoTitle}</CardTitle>
              </CardHeader>
            )}
            <CardContent className={branding?.welcomeVideoTitle ? 'pt-0 p-4' : 'p-0'}>
              <div 
                className="rounded-lg overflow-hidden bg-black relative"
                style={getVideoResponsiveStyle()}
              >
                {branding.welcomeVideoFile ? (
                  <video 
                    src={branding.welcomeVideoFile.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.welcomeVideoFile}` : branding.welcomeVideoFile} 
                    controls
                    playsInline
                    webkit-playsinline="true"
                    loop={branding.welcomeVideoLoop}
                    autoPlay={branding.welcomeVideoAutoplay}
                    muted={branding.welcomeVideoAutoplay}
                    preload="metadata"
                    className="w-full h-full object-cover"
                    style={{ 
                      ...getVideoCropStyles(),
                      background: 'black',
                      objectPosition: branding.welcomeVideoObjectPosition || '50% 50%'
                    }}
                  />
                ) : getYouTubeEmbedUrl() ? (
                  <iframe
                    src={getYouTubeEmbedUrl()}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 'none' }}
                    allowFullScreen
                    title={branding.welcomeVideoTitle || 'Video de presentation'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  />
                ) : null}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Trading Status & Risk Management */}
          <div className="space-y-6">
            {/* État du trading */}
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-1.5">
                  <CardTitle className="text-base font-semibold">État du trading</CardTitle>
                  <InfoTooltip text="Résumé de vos performances de trading sur la période sélectionnée. Les gains et pertes correspondent aux positions clôturées durant cette période. Le pourcentage est calculé par rapport à votre solde de trading." />
                </div>
              </CardHeader>
              <CardContent>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger className="w-full mb-4" style={{ borderColor: primaryColor }}>
                    <SelectValue placeholder="Sélectionner une période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aujourd'hui">Aujourd'hui</SelectItem>
                    <SelectItem value="7j">7 jours</SelectItem>
                    <SelectItem value="14j">14 jours</SelectItem>
                    <SelectItem value="1m">1 mois</SelectItem>
                    <SelectItem value="3m">3 mois</SelectItem>
                    <SelectItem value="6m">6 mois</SelectItem>
                    <SelectItem value="1an">1 an</SelectItem>
                  </SelectContent>
                </Select>
                
                {tradingPerformance && tradingPerformance[selectedPeriod] ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Gains/Pertes</p>
                      <div className="flex items-baseline gap-3">
                        <p className={`text-3xl font-bold ${tradingPerformance[selectedPeriod].profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {tradingPerformance[selectedPeriod].profit >= 0 ? '+' : ''}{tradingPerformance[selectedPeriod].profit.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </p>
                        {(() => {
                          const tBal = (tradingPortfolio?.available || tradingPortfolio?.balance || 0);
                          const profit = tradingPerformance[selectedPeriod].profit;
                          const base = tBal - profit; // balance before profit
                          const pct = base > 0 ? (profit / base * 100) : 0;
                          return (
                            <span className={`text-lg font-bold ${pct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              ({pct >= 0 ? '+' : ''}{pct.toFixed(2)}%)
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="w-4 h-4" />
                      <span>{tradingPerformance[selectedPeriod].trades_count} opération(s)</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-muted-foreground">Gains/Pertes</p>
                    <p className="text-xl sm:text-3xl font-bold">0,00 €</p>
                    <p className="text-xs text-muted-foreground mt-1">Aucune opération sur cette période</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gestion du risque */}
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Gestion du risque</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {/* Circular Progress */}
                <div className="relative w-32 h-32 mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-muted/20"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke={primaryColor}
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56 * (confidenceIndex / 100)} ${2 * Math.PI * 56}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold" style={{ color: primaryColor }}>{confidenceIndex}%</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center mb-4">
                  Ajuster le niveau de sécurité de vos transactions en fonction des tendances des marchés.
                </p>
                <div className="flex items-center gap-2 w-full">
                  <span className="text-sm">Indice de confiance</span>
                  <Input
                    type="number"
                    value={confidenceIndex}
                    onChange={(e) => setConfidenceIndex(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                    className="w-16 text-center"
                  />
                  <span className="text-sm">%</span>
                </div>
                <Button className="w-full mt-4" style={{ backgroundColor: primaryColor }} data-testid="validate-risk-btn">
                  Valider
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Middle Column - Historical Chart + Partners */}
          <div className="space-y-4">
            {/* Historical Chart - Reduced height */}
            <Card className="bg-card/80 backdrop-blur border-border/50">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-1.5">
                  <CardTitle className="text-base font-semibold">Évolution du capital</CardTitle>
                  <InfoTooltip text="Ce graphique retrace l'évolution de votre capital mois par mois, en tenant compte de vos dépôts, retraits, bonus, intérêts et gains de trading." />
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={generateChartData()}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={primaryColor} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={primaryColor} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      />
                      <YAxis 
                        axisLine={false} 
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                        tickFormatter={(v) => `${v}€`}
                        width={50}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke={primaryColor}
                        strokeWidth={2}
                        fill="url(#colorValue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Partners Section */}
            {partners.length > 0 && (
              <Card className="bg-card/80 backdrop-blur border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">Nos Partenaires</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center justify-center gap-4">
                    {partners.map((partner) => {
                      const sizeClasses = partner.logo_size === 'small' ? 'w-10 h-10' : 
                                          partner.logo_size === 'large' ? 'w-24 h-24' : 'w-16 h-16';
                      return (
                        <a 
                          key={partner.id} 
                          href={partner.website_url || '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="group transition-all duration-200 hover:scale-105"
                          title={partner.name}
                        >
                          <div className={`${sizeClasses} rounded-lg bg-white/10 p-2 flex items-center justify-center border border-border/30 group-hover:border-primary/50`}>
                            <img 
                              src={partner.logo_base64 || partner.logo_url} 
                              alt={partner.name}
                              className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<span class="text-xs text-center text-muted-foreground">${partner.name}</span>`;
                              }}
                            />
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Latest Positions */}
          <Card className="bg-card/80 backdrop-blur border-border/50 flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-1.5">
                <CardTitle className="text-base font-semibold">Dernières positions</CardTitle>
                <InfoTooltip text="Vos positions de trading les plus récentes. Les montants en vert sont des gains, en rouge des pertes. Seules les positions clôturées et visibles sont affichées ici." />
              </div>
              <button onClick={() => setShowPositionsModal(true)} className="text-xs text-muted-foreground hover:underline cursor-pointer">Voir tout</button>
            </CardHeader>
            <CardContent className="space-y-3 flex-1 overflow-y-auto min-h-[200px]">
              {(() => {
                const safePositions = Array.isArray(positions) ? positions : [];
                // Show CLOSED+CREDITED and VISIBLE positions
                const visiblePositions = safePositions.filter(p => 
                  (p.status === 'closed' && p.credited === true) || p.status === 'visible'
                );
                
                if (visiblePositions.length === 0) {
                  return (
                    <div className="text-center py-8 text-muted-foreground">
                      <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune position clôturée</p>
                    </div>
                  );
                }
                
                return visiblePositions
                  .sort((a, b) => {
                    const dateA = a.close_at || a.scheduled_date || a.trade_date || '';
                    const dateB = b.close_at || b.scheduled_date || b.trade_date || '';
                    return dateB.localeCompare(dateA);
                  })
                  .slice(0, 10).map((position, index) => {
                  const assetName = ASSET_NAMES[position.symbol] || position.symbol;
                  
                  return (
                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const logoUrl = ASSET_LOGOS[position.symbol];
                          const brandColor = ASSET_BRAND_COLORS[position.symbol];
                          if (logoUrl) {
                            return (
                              <div className="w-10 h-10 rounded-full overflow-hidden ring-1 ring-white/20 shadow-md bg-white/10 flex items-center justify-center">
                                <img src={logoUrl} alt={position.symbol} className="w-7 h-7 object-contain" onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                                <span className="hidden items-center justify-center text-xs font-bold w-full h-full">{position.symbol?.slice(0,2)}</span>
                              </div>
                            );
                          }
                          return (
                            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ring-1 ring-white/10" style={{ backgroundColor: brandColor || '#4B5563' }}>
                              {position.symbol?.slice(0, position.symbol.length > 3 ? 2 : 3)}
                            </div>
                          );
                        })()}
                        <div>
                          <p className="font-semibold">{position.symbol}</p>
                          <p className="text-xs text-muted-foreground">{assetName}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{position.trade_date}</p>
                        <p className="font-semibold" style={{ color: (position.profit || 0) >= 0 ? accentColor : '#ef5350' }}>
                          {(position.profit || 0) >= 0 ? '+' : ''}{(position.profit || 0).toFixed(2)} €
                        </p>
                      </div>
                    </div>
                  );
                });
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Section - Messages, Contact, Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Messagerie */}
          <Card className="bg-card/80 backdrop-blur border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold uppercase tracking-wider">Messagerie</CardTitle>
            </CardHeader>
            <CardContent className="text-center py-8">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-10 h-10 text-primary" />
              </div>
              <p className="text-muted-foreground mb-4">Votre messagerie est actuellement vide.</p>
              <Link to="/messages">
                <Button className="w-full" style={{ backgroundColor: primaryColor }} data-testid="send-message-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer un message
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Contactez-nous - Analyst Business Card */}
          <Card className="bg-card/80 backdrop-blur border-border/50">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold uppercase tracking-wider">Votre Conseiller</CardTitle>
              <Badge
                variant="outline"
                className="text-xs"
                style={{
                  borderColor: analystAvailability.isAvailable ? accentColor : '#f43f5e',
                  color: analystAvailability.isAvailable ? accentColor : '#f43f5e'
                }}
                data-testid="analyst-availability-badge"
              >
                {analystAvailability.label}
              </Badge>
            </CardHeader>
            <CardContent>
              {profile?.analyst ? (
                <div className="space-y-4">
                  {/* Analyst Profile Header */}
                  <div className="flex items-center gap-4">
                    {profile.analyst.profile_photo_url ? (
                      <img 
                        src={profile.analyst.profile_photo_url} 
                        alt={`${profile.analyst.first_name} ${profile.analyst.last_name}`}
                        className="w-16 h-16 rounded-full object-cover border-2"
                        style={{ borderColor: primaryColor }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<div class="w-16 h-16 rounded-full flex items-center justify-center" style="background-color: ${primaryColor}20"><svg class="w-7 h-7" style="color: ${primaryColor}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></div>`;
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                        <User className="w-7 h-7" style={{ color: primaryColor }} />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-lg">{profile.analyst.first_name} {profile.analyst.last_name}</p>
                      {profile.analyst.job_title && (
                        <p className="text-sm" style={{ color: primaryColor }}>{profile.analyst.job_title}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Contact Details */}
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    {/* Email */}
                    <a href={`mailto:${profile.analyst.email}`} className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="truncate">{profile.analyst.email}</span>
                    </a>
                    
                    {/* Phone */}
                    {profile.analyst.phone && (
                      <a href={`tel:${profile.analyst.phone}`} className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{profile.analyst.phone}</span>
                      </a>
                    )}
                    
                    {/* Phone Pro */}
                    {profile.analyst.phone_pro && (
                      <a href={`tel:${profile.analyst.phone_pro}`} className="flex items-center gap-3 text-sm hover:text-primary transition-colors">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <span>{profile.analyst.phone_pro} <span className="text-xs text-muted-foreground">(Pro)</span></span>
                      </a>
                    )}
                    
                    {/* WhatsApp */}
                    {profile.analyst.whatsapp && (
                      <a 
                        href={`https://wa.me/${profile.analyst.whatsapp.replace(/[^0-9]/g, '')}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 text-sm hover:text-green-500 transition-colors"
                      >
                        <svg className="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        <span>{profile.analyst.whatsapp}</span>
                      </a>
                    )}

                    <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="analyst-availability-status">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <span>{analystAvailability.detail}</span>
                    </div>
                    {analystAvailability.schedule && (
                      <div className="text-xs text-muted-foreground" data-testid="analyst-availability-schedule">
                        {analystAvailability.schedule}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                      <Shield className="w-7 h-7" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Support Client</p>
                      <p className="text-sm text-muted-foreground">Équipe dédiée</p>
                    </div>
                  </div>
                  <div className="space-y-2 pt-2 border-t border-border/50">
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span>support@privatebank.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>+33 1 87 68 03 10</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mes Documents - KYC Status with Real Data */}
          <Card className="bg-card/80 backdrop-blur border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold uppercase tracking-wider flex items-center justify-between">
                <span>Mes Documents</span>
                {kycStatus?.is_complete && (
                  <Badge className="bg-green-500/20 text-green-500 text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Validé
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {kycDocs.length === 0 ? (
                <div className="text-center py-4">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                  <p className="text-sm text-muted-foreground">Aucun document téléversé</p>
                  <Link to="/client/profile" className="text-xs underline mt-1 block" style={{ color: primaryColor }}>
                    Ajouter des documents
                  </Link>
                </div>
              ) : (
                <>
                  {kycDocs.slice(0, 4).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm truncate max-w-[120px]">
                          {doc.document_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <Badge 
                        variant="outline"
                        className={`text-xs ${
                          doc.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          doc.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}
                      >
                        {doc.status === 'approved' ? (
                          <><CheckCircle2 className="w-3 h-3 mr-1" />Validé</>
                        ) : doc.status === 'rejected' ? (
                          <><XCircle className="w-3 h-3 mr-1" />Refusé</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" />En attente</>
                        )}
                      </Badge>
                    </div>
                  ))}
                  <Link 
                    to="/client/profile" 
                    className="block text-center text-xs py-2 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                    style={{ color: primaryColor }}
                  >
                    Voir tous les documents →
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Featured News */}
        {featuredNews.length > 0 && (
          <div data-testid="featured-news-section">
            <h2 className="text-base font-semibold uppercase tracking-wider mb-4" style={{ color: primaryColor }}>
              Actualités à la une
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredNews.map(article => (
                <Link key={article.id} to={`/client/news?article=${article.id}`} className="group" data-testid={`featured-news-card-${article.id}`}>
                  <Card className="bg-card/80 backdrop-blur border-border/30 overflow-hidden hover:border-primary/40 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/5 h-full">
                    {article.cover_image && (
                      <div className="h-40 overflow-hidden">
                        <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{article.category}</Badge>
                        {article.published_at && (
                          <span className="text-[10px] text-muted-foreground">
                            {new Date(article.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                      <h3 className="font-bold text-sm text-white leading-snug group-hover:text-primary transition-colors line-clamp-2">{article.title}</h3>
                      {article.summary && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{article.summary}</p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Featured Placements */}
        {featuredProducts.length > 0 && (
          <div data-testid="featured-placements-section">
            <h2 className="text-base font-semibold uppercase tracking-wider mb-4" style={{ color: primaryColor }}>
              Placements à la une
            </h2>
            <div className={`grid gap-4 ${featuredProducts.length === 1 ? 'grid-cols-1 max-w-lg' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
              {featuredProducts.map(product => (
                <Link key={product.id} to={`/client/placements/product/${product.id}`} className="group" data-testid={`featured-product-card-${product.id}`}>
                  <Card className="bg-card/80 backdrop-blur border-border/30 overflow-hidden hover:border-primary/40 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/5">
                    {product.images?.[0] && (
                      <div className="h-36 overflow-hidden">
                        <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    )}
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-bold text-white group-hover:text-primary transition-colors">{product.name}</h3>
                          <p className="text-xs text-muted-foreground">{product.category?.title || product.subcategory}</p>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400 border-0 shrink-0">
                          {product.rentability_rate}%
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/20">
                        <span>Durée : {product.duration_months} mois</span>
                        <span>Min. {product.min_investment?.toLocaleString('fr-FR')} €</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Welcome Video Section - Bottom Position */}
        {branding?.welcomeVideoEnabled && (branding?.welcomeVideoUrl || branding?.welcomeVideoEmbed || branding?.welcomeVideoFile) && branding?.welcomeVideoPosition === 'bottom' && (
          <Card className="bg-card/80 backdrop-blur border-border/50 overflow-hidden">
            {branding?.welcomeVideoTitle && (
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{branding.welcomeVideoTitle}</CardTitle>
              </CardHeader>
            )}
            <CardContent className={branding?.welcomeVideoTitle ? 'pt-0 p-4' : 'p-0'}>
              <div 
                className="rounded-lg overflow-hidden bg-black relative"
                style={getVideoResponsiveStyle()}
              >
                {branding.welcomeVideoFile ? (
                  <video 
                    src={branding.welcomeVideoFile.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${branding.welcomeVideoFile}` : branding.welcomeVideoFile} 
                    controls
                    playsInline
                    webkit-playsinline="true"
                    loop={branding.welcomeVideoLoop}
                    autoPlay={branding.welcomeVideoAutoplay}
                    muted={branding.welcomeVideoAutoplay}
                    preload="metadata"
                    className="w-full h-full object-cover"
                    style={{ 
                      ...getVideoCropStyles(),
                      background: 'black',
                      objectPosition: branding.welcomeVideoObjectPosition || '50% 50%'
                    }}
                  />
                ) : getYouTubeEmbedUrl() ? (
                  <iframe
                    src={getYouTubeEmbedUrl()}
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 'none' }}
                    allowFullScreen
                    title={branding.welcomeVideoTitle || 'Video de presentation'}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  />
                ) : null}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
